import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Maximize2, SlidersHorizontal } from "lucide-react";

import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { resolveCompliance } from "@/entities/compliance";
import { AdvisorChatPanel, buildSessionId, useAdvisorAccess } from "@/entities/advisor-chat";
import { matchScene, type Scene } from "@/shared/lib/advisorScene";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { useCreationBadge } from "./useCreationBadge";
import { useAdvisorLauncher } from "./AdvisorLauncherContext";
import { AdvisorRobot } from "./AdvisorRobot";
import { useLauncherDock } from "./useLauncherDock";
import { useRobotMotion, type RobotMode } from "./useRobotMotion";
import { usePersona } from "@/shared/brand/persona";

/** 无操作多久后淡隐(让出身下内容,如表格分页器)。 */
const IDLE_DIM_MS = 8000;

/**
 * 全局悬浮球副驾:被授权 agent_access 的租户老板/经理在任何 dashboard 页随手唤起小阳。
 * 门控见 useAdvisorAccess(非代客 + 租户身份 + agentAccess)。挂在 AdminLayout,代客/无授权时整体不渲染。
 *
 * <p>可拖动(2026-07-15):球定位改为 fixed left-0 top-0 + transform 位移,位置由 useLauncherDock
 * 管理(吸边 + 比例锚定 + localStorage)。老版钉死 bottom-6 right-6 恰好压表格分页器。
 *
 * <p>★本组件只做门控/会话/场景快照,球本体拆成 AdvisorBall:agentAccess 是异步的,首帧
 * canUseAdvisor=false → 若球的 hooks 挂在这里,落位 effect 会在按钮还没进 DOM 时空跑一次,
 * 而按钮后来挂载并不会让依赖变化 → effect 永不重跑,球永远留在 transform 原点(左上角)。
 * 拆开后球的 hooks 与球同生命周期,ref 必然先于 effect 绑好。
 */
/** 军师满屏页路由 —— 与 App.tsx 里那条 `<Route path="/advisor/chat">` 同一个字面量。 */
const ADVISOR_FULLPAGE_PATH = "/advisor/chat";

export function AdvisorLauncher() {
  const persona = usePersona();
  const { canUseAdvisor } = useAdvisorAccess();
  const { user, effectiveRole, permissions } = useAuth();
  // 1011 算法备案:军师回复下方「内容由AI生成」(缺省兜底开)
  const showAiLabel = resolveCompliance(permissions?.compliance).visibleLabel;
  const canOpenHub = isTenantManagerPlus(effectiveRole);
  // open 状态上提到共享 context —— 工作台卡片/快捷也能唤起同一浮层(见 AdvisorLauncherContext)。
  const { open, setOpen, restoreFocus, scrolledPastHero } = useAdvisorLauncher();
  // 关闭态出图完成红点(问题3):hooks 规则要求无条件调用,无权限用户由 enabled 门挡掉轮询。
  const { hasUnseen } = useCreationBadge(open, canUseAdvisor);
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  // 场景快照:只在 open false→true 那一刻取,面板存活期间冻结(开着时切页不重排网格)。
  const [scene, setScene] = useState<Scene | null>(null);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  useEffect(() => {
    if (open) setScene(matchScene(pathRef.current));
  }, [open]);

  if (!canUseAdvisor) return null;
  // ADV-UI-ENTRY-1(2026-08-26 用户报「电脑端把小阳展开之后悬浮机器人还在」):
  // 军师满屏页**就是**军师,页面里再挂一个「进军师」的球 = 门里面还有一扇同样的门。
  // ★不是把球删掉:别的页面它仍是「随手问一句」的快捷方式,这里只是不重复。
  // ★判据钉**路由**不钉「浮层开没开」——展开整页与浮层是两条路(整页可以直接深链进来,
  //   那时 open 恒 false),钉 open 会漏掉深链那一支。
  if (pathname === ADVISOR_FULLPAGE_PATH) return null;
  const sessionId = buildSessionId(user);
  // 首页顶部:hero 就是小阳本体,悬浮球藏(下滑越过 hero → scrolledPastHero=true 才现)。其它页恒显。
  const hiddenAtHeroTop = pathname === "/" && !scrolledPastHero;

  return (
    <>
      <AdvisorBall
        open={open}
        hasUnseen={hasUnseen}
        hiddenAtHeroTop={hiddenAtHeroTop}
        onOpen={() => setOpen(true)}
      />

      <Sheet open={open} onOpenChange={setOpen}>
        {/* 加宽 + 全高:给海报画布腾空间(问题1)。宽屏 max-w-2xl≈672px、更宽屏 3xl≈768px;窄屏 90vw 仍留边不盖主页。 */}
        <SheetContent
          side="right"
          className="flex w-[90vw] max-w-2xl flex-col sm:max-w-3xl"
          // preventDefault 掉 Radix 那句 `triggerRef.current?.focus()`(本浮层无 Trigger,那句是 no-op,
          // 焦点会掉 body)。composeEventHandlers 先跑我们、defaultPrevented 后就不跑 Radix 的。
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            restoreFocus();
          }}
        >
          <SheetHeader>
            <div className="flex items-center justify-between gap-3 pr-6">
              <SheetTitle>
                {persona.title}
                {persona.name}
              </SheetTitle>
              <div className="flex items-center gap-3">
                {/* B1(2026-08-17):展开整页 —— 浮层是「随手问一句」,想沉浸就整屏。
                    agent_access 即可(与满屏页同门),同一会话无缝续上。 */}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    // ADV-UI-6:带上来源路由,满屏页「返回/缩成小窗」优先回这儿(不带就只能退历史)。
                    navigate("/advisor/chat", {
                      state: { from: location.pathname + location.search },
                    });
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                  展开整页
                </button>
                {/* 经营看板入口(2026-07-30 四合一):看盘/对话记录/记忆/技能都在那儿。MANAGER+ 才给
                    —— STAFF 没有 /advisor 的路由白名单,给了就是一条点了 403 的链接。 */}
                {canOpenHub && (
                  <Link
                    to="/advisor"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    经营看板
                  </Link>
                )}
              </div>
            </div>
          </SheetHeader>
          <div className="mt-4 flex-1 overflow-hidden">
            <AdvisorChatPanel sessionId={sessionId} scene={scene} showAiLabel={showAiLabel} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/** 球本体:拖动 + 机器人动效。只在有权限时挂载(见 AdvisorLauncher 注释)。 */
function AdvisorBall({
  open,
  hasUnseen,
  hiddenAtHeroTop,
  onOpen,
}: {
  open: boolean;
  hasUnseen: boolean;
  hiddenAtHeroTop: boolean;
  onOpen: () => void;
}) {
  const persona = usePersona();
  const ballRef = useRef<HTMLButtonElement>(null);
  // 造型跟随当前皮肤(读 DOM data-skin,切皮肤即时生效 —— 不能用 useTheme(),它的状态是每实例独立的)
  const skin = useActiveSkin();
  const [hovered, setHovered] = useState(false);
  const [dimmed, setDimmed] = useState(false);
  // dragging 由本组件持有:mode(要 dragging)→ motion → dock(产出 dragging)会成环,
  // 故 dock 只回调上报,状态留在这里。
  const [dragging, setDragging] = useState(false);

  const mode: RobotMode = dragging ? "drag" : hovered ? "hover" : "idle";
  const motion = useRobotMotion(ballRef, mode);
  const { announcement, handlers } = useLauncherDock({
    ref: ballRef,
    onTap: onOpen,
    motion,
    onDraggingChange: setDragging,
  });

  // 有新作品那一刻跳一下(上升沿触发,不是持续状态 —— 否则红点亮着期间球会僵住不呼吸)。
  const prevUnseen = useRef(hasUnseen);
  useEffect(() => {
    if (hasUnseen && !prevUnseen.current) motion.notify();
    prevUnseen.current = hasUnseen;
  }, [hasUnseen, motion]);

  // 空闲淡隐:无交互 8s → 半透明,让出身下内容;有交互/新作品即恢复。
  useEffect(() => {
    setDimmed(false);
    if (hovered || dragging || open || hasUnseen) return;
    const t = setTimeout(() => setDimmed(true), IDLE_DIM_MS);
    return () => clearTimeout(t);
  }, [hovered, dragging, open, hasUnseen]);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={ballRef}
            type="button"
            aria-label={`打开${persona.title}${persona.name}`}
            aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight Alt+ArrowUp Alt+ArrowDown"
            // 首页顶部球隐身时:CSS 隐还不够,得退出 tab 序 + 对读屏隐藏(否则键盘用户 tab 到看不见的控件)。
            tabIndex={hiddenAtHeroTop ? -1 : undefined}
            aria-hidden={hiddenAtHeroTop || undefined}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            {...handlers}
            // 皮肤隔离钩子:球的 position/transform 归 dock+GSAP 独占,皮肤不得染指。
            // 见 index.css 的 [data-advisor-launcher] 不变量块(2026-07-15 comic 事故)。
            data-advisor-launcher=""
            // fixed 到左上角 + transform 位移:让 gsap 只写 transform(不触发重排)。
            // touch-none 防移动端拖球时页面跟着滚。
            className={`fixed left-0 top-0 z-40 flex h-14 w-14 touch-none select-none items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-opacity duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              dragging ? "cursor-grabbing" : "cursor-grab"
            } ${open || hiddenAtHeroTop ? "pointer-events-none opacity-0" : dimmed ? "opacity-60" : "opacity-100"}`}
          >
            {/* rig 层:入场动画专用。root 的 transform 由停靠位独占,不能再挂别的 tween。 */}
            <span data-part="rig" className="flex items-center justify-center">
              <AdvisorRobot variant={skin} className="h-9 w-9" />
            </span>
            {hasUnseen && (
              <span
                aria-label="有新作品"
                className="absolute right-1 top-1 h-3 w-3 rounded-full bg-destructive ring-2 ring-background"
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">拖我可以换位置 · Alt+方向键也行</TooltipContent>
      </Tooltip>

      {/* 拖动是纯视觉动作,读屏用户靠这里知道落在哪边 */}
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </>
  );
}
